import React from 'react';
import { StaticQuery, graphql } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import {
  Text,
  WorkSectionWrapper,
  WorkSectionDescription,
  WorkSectionContentWrapper,
  ContentImageCentered,
  ContentImageFull,
  ContentTextSection,
  HighlightPoint,
  HighlightWrapper,
} from '../components';

interface WorkMinBedriftProps {}

export const WorkMinBedrift: React.FC<WorkMinBedriftProps> = () => (
  <StaticQuery
    query={graphql`
      query {
        mockupImage1: file(
          relativePath: {
            eq: "images/Telia/MB3 Mockup Subscription iPhone 14 Pro.png"
          }
        ) {
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, webpOptions: { quality: 40 })
          }
        }
        mockupImagePhones: file(
          relativePath: { eq: "images/Telia/MB3 phones.png" }
        ) {
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, webpOptions: { quality: 40 })
          }
        }
        mockupImageBrowsers: file(
          relativePath: { eq: "images/Telia/MB3 Browsers Light.png" }
        ) {
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, webpOptions: { quality: 40 })
          }
        }
        mockupSketch1: file(
          relativePath: { eq: "images/Telia/MB3 Column of Skecthes.png" }
        ) {
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, webpOptions: { quality: 40 })
          }
        }
        personasImage: file(
          relativePath: { eq: "images/Telia/MB3 Personas.png" }
        ) {
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, webpOptions: { quality: 40 })
          }
        }
        sprintImage: file(relativePath: { eq: "images/Telia/MB3 sprint.png" }) {
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, webpOptions: { quality: 40 })
          }
        }
        testImage: file(
          relativePath: { eq: "images/Telia/MB3 user tests.png" }
        ) {
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, webpOptions: { quality: 40 })
          }
        }
        needsImage: file(relativePath: { eq: "images/Telia/MB3 needs.png" }) {
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, webpOptions: { quality: 40 })
          }
        }
      }
    `}
    render={(data) => {
      const HeaderImage = data ? getImage(data.mockupImage1) : null;
      const HeaderImageAlt =
        'An image of two iPhone 14s with an app showing mobile subscriptions.';

      const SecondImage = data ? getImage(data.mockupImageBrowsers) : null;
      const SecondImageAlt =
        'An image of two browser windows showing the content of Min Bedrifts landing page.';

      const MockupImagePhones = data ? getImage(data.mockupImagePhones) : null;
      const MockupImagePhonesAlt =
        'An image of three iPhone 13s with the user interface displayed on them..';

      const MockupSketch1 = data ? getImage(data.mockupSketch1) : null;
      const MockupSketch1Alt =
        'An image of 6 sketches showing off various parts of the user interface.';

      const PersonasImage = data ? getImage(data.personasImage) : null;
      const PersonasImageAlt =
        'An image of two of our core users and their needs.';

      const SprintImage = data ? getImage(data.sprintImage) : null;
      const SprintImageAlt =
        'An image of sticky notes showing the initial work process';

      const TestsImage = data ? getImage(data.testImage) : null;
      const TestsImageAlt = 'An image of notes from a series of user test';

      const NeedsImage = data ? getImage(data.needsImage) : null;
      const NeedsImageAlt =
        'An image from a workshop displaying sticky notes on a digital whiteboard. ';

      return (
        <WorkSectionWrapper>
          <WorkSectionDescription
            title="Telia Min Bedrift"
            role="UX Lead"
            createdWith={[
              <a
                key="TeliaLink"
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.telia.no"
              >
                Telia Norge
              </a>,
              ' and ',
              <a
                key="BredvidLink1"
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.bredvid.no"
              >
                Bredvid
              </a>,
            ]}
            work="Product strategy, UX research, Prototyping, Graphic design, Workshop facilitation, Coaching"
            link={
              <a
                key="TeliaLinkBedrift"
                target="_blank"
                rel="noopener noreferrer"
                href="https://bedrift.telia.io"
              >
                bedrift.telia.io
              </a>
            }
          >
            In 2020, Telia wanted to be establish themselves as the prime
            ICT-provider for Norwegian enterprises.
            <br />
            <br />
            They wanted to achieve this by helping their customers communicate
            effectively through a connected digital infrastructure — Phones,
            mobile subscriptions, fiber, VPNs, TV, 5G-powered IoT devices —
            Telia would be the place to get all your needs covered.
            <br />
            <br />
            As lead designer in a cross-functional product team, I worked to
            create a holistic self-service platform to equip people in 26 000
            enterprises with the tools they need, and empower them to keep
            things running smoothly.
          </WorkSectionDescription>
          <WorkSectionContentWrapper>
            <ContentImageCentered
              image={HeaderImage}
              imageAlt={HeaderImageAlt}
              eager
            ></ContentImageCentered>
            <ContentTextSection>
              <Text heading3 alignCenter as="h2" pb="40px">
                Backdrop
              </Text>
              <Text body1 as="p">
                Through talking to customers, we learned about their challenges
                and issues. We discovered that a majority of their frustrations
                stem from things not being coherent.
              </Text>
              <HighlightWrapper>
                <HighlightPoint icon="microphone">
                  <Text body1 italic as="p">
                    “When I make a change in one of your systems, it doesn’t
                    affect my mobile subscriptions in the other one. I have to
                    keep copy-pasting to keep things in sync. It was annoying in
                    the beginning, but I've gotten used to it by now.”
                  </Text>
                  <Text body2 as="p">
                    - Anonymous user 3
                  </Text>
                </HighlightPoint>
                <HighlightPoint icon="microphone">
                  <Text body1 italic as="p">
                    "When working on stuff I have two screens up at the same
                    time; one with the current Min bedrift, and two
                    excel-spreadsheets on the other. I tend to download data
                    from Min bedrift, and that goes into a third excel-sheet.
                    Then i have notepad — well, notepad++ is what I’m using — to
                    take some notes and write summaries while I’m working. It’s
                    a strange way of doing it I guess, but it’s how I get it
                    done."
                  </Text>
                  <Text body2 as="p">
                    - Anonymous user 4
                  </Text>
                </HighlightPoint>
                <HighlightPoint icon="microphone">
                  <Text body1 italic as="p">
                    “I want to teach myself how to manage all of this better,
                    but I just don't have the time.”
                  </Text>
                  <Text body2 as="p">
                    - Anonymous user 6
                  </Text>
                </HighlightPoint>
              </HighlightWrapper>

              <Text body1 as="p">
                We observed that when self-service products are available, our
                users had to update a lot of information in multiple systems.
                The experience was fragmented.
                <br />
                <br />
                If no systems existed, they used excel, emails, phone calls or
                their internal connections at Telia to manage their products and
                services for them.
                <br />
                <br />
                This fragmented experience increased the workload, and raised
                the threshold for self servicing. Our users felt overwhelmed by
                the work, and it was time-consuming. It was easier to have Telia
                perform manual work for them, or to not do anything at all.
                <br />
                <br />
                This also had a nasty side effect; Our user blamed themselves
                for the poor experience. They saw the potential for this working
                well, but still felt like they were at fault for not putting in
                enough effort.
              </Text>
            </ContentTextSection>
            <ContentImageFull
              image={PersonasImage}
              imageAlt={PersonasImageAlt}
              eager
              description={'Two of our users. Anonymized.'}
            ></ContentImageFull>
            <ContentTextSection>
              <Text heading3 alignCenter as="h2" pb="40px">
                Opportunity
              </Text>
              <Text body1 as="p">
                How might we empower people in Norwegian enterprises to
                experience coherent control of their digital communication?
                <br></br>
                <br></br>
                We believed that by building a product around our users mental
                models and how they looked at their organizations and needs, we
                would improve the user experience and create sensations of
                safety, coherence and trust.
              </Text>
            </ContentTextSection>
            <ContentImageFull
              image={SprintImage}
              imageAlt={SprintImageAlt}
              eager
              description={'The aftermath of our initial team workshop.'}
            ></ContentImageFull>
            <ContentTextSection>
              <Text heading3 alignCenter as="h2" pb="40px">
                Testing
              </Text>
              <Text body1 as="p">
                We challenged our assumptions through multiple iterations of
                prototyping and testing. We quickly discovered what we got
                right, what we got wrong, and adapted to what we learned.
                <br></br>
                <br></br>
                We found that our initial assumptions were on the right track,
                but we discovered nuances that helped us tweak the product to
                attain better product-market fit.
              </Text>
            </ContentTextSection>
            {/* <ContentImageFull
              image={NeedsImage}
              imageAlt={NeedsImageAlt}
              description={'Map of some of the needs we discovered.'}
            ></ContentImageFull> */}
            <ContentImageFull
              image={TestsImage}
              imageAlt={TestsImageAlt}
              description={'Notes from a set of user tests.'}
            ></ContentImageFull>
            <ContentTextSection>
              <Text body1 as="p">
                Since the product is built around the customers lasting
                constructs — their departments, their places, and most
                importantly, their people — it's likely to remain a robust
                platform in the future.
                <br></br>
                <br></br>
                Over time it could enable customers to manage the entirety of
                their digitial communication needs. All from their own
                perspectives, and around their needs.
                <br></br>
                <br></br>
                This would enable Telia to adapt future products around their
                customers. It would secure them with a value enabling,
                sustainable service platform for years to come.
              </Text>
            </ContentTextSection>
            <ContentImageFull
              image={SecondImage}
              imageAlt={SecondImageAlt}
              eager
              description={'Empower people to communicate efficiently.'}
            ></ContentImageFull>
            <ContentImageCentered
              image={MockupSketch1}
              imageAlt={MockupSketch1Alt}
              eager
              description={
                'Manage people, places, products, orders and spendings.'
              }
            ></ContentImageCentered>
            <ContentImageCentered
              image={MockupImagePhones}
              imageAlt={MockupImagePhonesAlt}
              eager
              description={'Telia Min Bedrift'}
            ></ContentImageCentered>
          </WorkSectionContentWrapper>
        </WorkSectionWrapper>
      );
    }}
  />
);
